import { usePriceContext } from 'contexts';
import { usePriceFormatter } from 'hooks/format';
import type { PriceModel } from 'models/price';
import type { HTMLAttributes } from 'types';
import { cn } from 'utils/classNames';
import { useI18n } from 'utils/i18n';
import { makeSpacesNoBreak } from 'utils/string';

import { getValueSymbolPrice } from './helpers';

interface Props extends HTMLAttributes<HTMLSpanElement> {
	as?: 'p' | 'span';
	className?: string;
	isReducedPrice?: boolean;
	prefix?: string;
	price: PriceModel | undefined;
}

/**
 * Formats a price and its symbol as plain text. Includes a more screen reader
 * friendly version.
 */
export default function PriceText({
	as = 'span',
	className,
	'data-cy': dataCy,
	isReducedPrice,
	prefix,
	price,
	...attrs
}: Props) {
	const { t } = useI18n();
	const { usePriceExcVat } = usePriceContext();
	const formatPrice = usePriceFormatter();
	const displayPrice = getValueSymbolPrice(price, usePriceExcVat);

	if (!displayPrice?.displayValue) {
		return null;
	}

	const formattedPrice = formatPrice(displayPrice.displayValue, 'regular');
	const priceSymbol = displayPrice.displaySymbol;

	// Price objects aren't caught in the first early return.
	if (!formattedPrice) {
		return null;
	}

	const Component = as === 'span' ? 'span' : 'p';

	// Position the screen reader text to make the visual screen reader cursor
	// a bit better. Skip when there is a prefix since that makes the price
	// position unknown.
	// Add the paragraph role to distinguish it from 'non content spans'.
	// This is needed for target selection in FocusTarget.
	return (
		<Component
			{...attrs}
			role={as === 'span' ? 'paragraph' : undefined}
			className={cn(
				'relative',
				isReducedPrice && 'text-reducedPrice',
				className,
			)}
		>
			{prefix && `${prefix} `}
			<span className={cn('sr-only', !prefix && 'top-[10%] left-0')}>
				{formattedPrice} {t('screen_reader_currency_symbol')}
			</span>
			<span aria-hidden data-cy={dataCy}>
				{makeSpacesNoBreak(`${formattedPrice}${priceSymbol || ''}`)}
			</span>
		</Component>
	);
}
PriceText.displayName = 'PriceText';
